<template>
  <div class="receipts-management text-center">
    <BreadCrumb class="d-print-none" :items="breadcrumbItems" />

    <v-card elevation="1" class="pt-5 mt-1 d-print-none" style="position: relative">
      <div v-if="!loading" class="d-flex justify-space-between align-center px-9 pb-6">
        <h1 v-if="isShipperDeliveryReceiptManagement">Imprimir canhotos</h1>
        <h1 v-else>Gerenciar canhotos {{ printMode ? '- Modo de impressão' : '' }}</h1>
        <div class="d-flex align-center" style="gap: 15px">
          <v-checkbox v-model="showNoReceiptNfes" label="Mostrar NFEs sem canhoto" hide-details class="ma-0 pa-0" />
          <v-checkbox
            :input-value="areAllReceiptsSelected"
            @click="handleSelectAll"
            label="Selecionar todos"
            hide-details
            class="ma-0 pa-0"
            :disabled="receipts.length === 0"
          />

          <v-btn-toggle v-model="selectedColumns" color="gray" mandatory class="mt-1">
            <v-btn small value="one">
              <v-icon color="#666" small>mdi-view-agenda</v-icon>
            </v-btn>
            <v-btn small value="two">
              <v-icon color="#666" small>mdi-view-grid</v-icon>
            </v-btn>
          </v-btn-toggle>

          <v-btn v-if="!isShipperDeliveryReceiptManagement" @click="handlePrintMode" small class="px-0" color="primary">
            <v-icon class="px-0" size="20" dark> {{ printIcon }} </v-icon>
          </v-btn>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="restoreToPrint"
                :disabled="savedReceipts.length === receipts.length"
                small
                class="px-0"
                color="primary"
                v-on="on"
                v-bind="attrs"
              >
                <v-icon class="px-0" size="20" dark> mdi-restore </v-icon>
              </v-btn>
            </template>
            <span>Restaurar canhotos para impressão</span>
          </v-tooltip>
        </div>
      </div>

      <div v-if="loading" class="d-flex flex-column align-center pt-2">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="py-4">Carregando canhotos...</p>
      </div>

      <v-row
        v-else-if="receipts.length"
        class="print-wrapper d-flex px-4 pb-4 receipts"
        dense
        style="height: 70vh; overflow-y: auto"
      >
        <v-col v-for="(receipt, i) in orderedByNfeReceipts" :key="receipt.id + i" :cols="columns[selectedColumns]">
          <div>
            <receipt
              :receipt="receipt"
              @approveReceipt="approveReceipt"
              @deleteReceipt="openDeleteDialog"
              @receiptAppend="onReceiptAppend"
              @removeFromPrint="onRemoveFromPrint"
            />
          </div>
        </v-col>
      </v-row>
      <div class="pb-5 pt-6" v-if="receipts.length">
        <div class="d-flex justify-center" style="gap: 20px">
          <template v-if="receipts.length > 0 && !loading && !printMode">
            <v-btn
              @click="openUpdateCompletionDateDialog()"
              :disabled="!hasSomeReceiptSelected || shouldLimitReceiptsSelection"
              color="primary"
              :dark="hasSomeReceiptSelected"
            >
              ALTERAR DATA DE FINALIZAÇÃO
            </v-btn>
            <v-btn
              @click="openDeleteDialog()"
              :disabled="!hasSomeReceiptSelected || shouldLimitReceiptsSelection"
              color="red"
              :dark="hasSomeReceiptSelected"
            >
              DELETAR CANHOTOS
            </v-btn>
            <v-btn
              @click="openApproveDialog()"
              :disabled="!hasSomeReceiptSelected || shouldLimitReceiptsSelection"
              color="green"
              :dark="hasSomeReceiptSelected"
            >
              APROVAR CANHOTOS
            </v-btn>
          </template>
          <template v-else-if="receipts.length > 0 && !loading && printMode">
            <v-btn
              :disabled="!hasSomeReceiptSelected"
              :dark="hasSomeReceiptSelected"
              @click="removeMultipleReceiptsFromPrint"
              color="red"
            >
              REMOVER DA IMPRESSÃO
            </v-btn>
            <v-btn @click="printReceipts" color="primary" dark> IMPRIMIR </v-btn>
          </template>
        </div>
      </div>

      <div v-if="savedReceipts.length && !receipts.length && !loading" class="py-6">
        <p>Todos canhotos removidos da impressão!</p>
      </div>
      <div v-else-if="receipts.length === 0 && !loading" class="py-6">
        <p>Nenhum canhoto encontrado!</p>
      </div>
      <div v-if="shouldLimitReceiptsSelection" class="pb-5" style="color: #929292">
        <span>Por favor, selecione menos de 250 canhotos para aprovar/deletar</span>
      </div>
    </v-card>
    <delete-confirm-dialog
      :isOpen.sync="isDeleteDialogOpen"
      :title="deleteConfirmTitleText"
      :bodyText="deleteConfirmBodyText"
      :isConfirmBtnLoading="isDeleteButtonLoading"
      @cancelClick="resetIdToDelete()"
      @confirmClick="handleDeleteReceipt()"
    />
    <approve-confirm-dialog
      :isOpen.sync="isApproveDialogOpen"
      :title="approveConfirmTitleText"
      :bodyText="approveConfirmBodyText"
      :isConfirmBtnLoading="isApproveButtonLoading"
      @cancelClick="resetIdToApprove()"
      @confirmClick="approveMultipleReceipts()"
    />

    <div class="w-100">
      <div v-for="(receipt, i) in orderedByNfeReceipts" :key="receipt.id + i" class="w-100 wrapper-to-print">
        <receipt-to-print :receipt="receipt" />
      </div>
    </div>

    <UpdateCompletionDate
      :open.sync="isUpdateCompletionDateDialogOpen"
      :nfeIds="nfeIds"
      @shouldUpdateList="shouldUpdateList()"
    />

    <MessagesError :type="errorsType" :errors="errors" @closeMessages="errors = []" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BreadCrumb from '@/components/BreadCrumb.vue';
import Base64Mixin from '@/mixins/base64.mixin';
import DeleteConfirmDialog from '@/components/ConfirmDialog';
import ApproveConfirmDialog from '@/components/ConfirmDialog';
import MessagesError from '@/components/MessagesError';
import Receipt from '@/components/Shipment/ManagementReceipt.vue';
import ReceiptToPrint from '@/components/Shipment/ManagementReceiptToPrint';
import { ReceiptsManagementHandler } from '@/store/modules/receiptsManagement';
import UpdateCompletionDate from '@/components/Nfe/UpdateCompletionDate.vue';
import { orderBy } from 'lodash';
import { baseURL } from '@/api/index';

export default {
  name: 'ShipmentsReceiptManagement',

  mixins: [Base64Mixin],
  components: {
    BreadCrumb,
    DeleteConfirmDialog,
    ApproveConfirmDialog,
    MessagesError,
    Receipt,
    ReceiptToPrint,
    UpdateCompletionDate
  },
  data: () => ({
    nfeIdToUpload: null,
    receiptToUpload: null,
    receiptToUploadPreview: null,
    showNoReceiptNfes: true,
    isApproveButtonLoading: false,
    isDeleteButtonLoading: false,
    isUpdateCompletionDateDialogOpen: false,
    isDeleteDialogOpen: false,
    isApproveDialogOpen: false,
    receiptIdToDelete: null,
    receiptIdToApprove: null,
    selectedReceipts: [],
    nfes: [],
    receipts: [],
    nfeIds: [],
    savedReceipts: [],
    noImgReceipts: [],
    error500: null,
    errors: [],
    errorsType: 'success',
    loading: true,
    columns: {
      one: '12',
      two: '6'
    },
    selectedColumns: 'one'
  }),
  computed: {
    ...mapGetters('NfeStore', ['getSelectedChecbox']),

    selectedReceiptsObjectValues() {
      return Object.values(ReceiptsManagementHandler.getSelectedReceipts(this));
    },
    areAllReceiptsSelected() {
      return this.selectedReceiptsObjectValues.every((value) => value);
    },
    hasSomeReceiptSelected() {
      return this.selectedReceiptsObjectValues.some((value) => value);
    },
    isAllReceiptsSelected() {
      const receiptsWithImage = this.receipts.filter((receipt) => receipt.srcImage !== null);
      return this.selectedReceipts.length === receiptsWithImage.length;
    },
    hasOneReceiptSelected() {
      return this.selectedReceipts.length <= 1;
    },
    approveConfirmTitleText() {
      return `Aprovar Canhoto${this.hasOneReceiptSelected || this.receiptIdToApprove ? '' : 's'}`;
    },
    approveConfirmBodyText() {
      const receipts = this.getSelectedReceipts();
      return `Você tem certeza que deseja aprovar estes canhotos(${receipts.length})?`;
    },
    deleteConfirmTitleText() {
      return `Deletar Canhoto${this.hasOneReceiptSelected || this.receiptIdToDelete ? '' : 's'}`;
    },
    deleteConfirmBodyText() {
      const receipts = this.getSelectedReceipts();
      if (this.receiptIdToDelete !== null) {
        return 'Você tem certeza que deseja deletar este canhoto?';
      }
      return `Você tem certeza que deseja deletar estes canhotos(${receipts.length})?`;
    },
    shouldLimitReceiptsSelection() {
      return this.selectedReceiptsObjectValues.filter((value) => value).length >= 250;
    },
    orderedByNfeReceipts() {
      return orderBy(this.receipts, ['numberNfe'], ['asc']);
    },
    printMode() {
      return ReceiptsManagementHandler.getPrintMode(this);
    },
    printIcon() {
      return this.printMode ? 'mdi-close' : 'mdi-printer';
    },
    isShipperDeliveryReceiptManagement() {
      return this.$route.name === 'ShipperDeliveryReceiptManagement';
    },
    breadcrumbItems() {
      if (this.isShipperDeliveryReceiptManagement) {
        return [
          {
            text: 'Início',
            disabled: false,
            to: '/start'
          },
          {
            text: 'Entregas',
            disabled: false,
            to: '/shipper-deliveries',
            exact: true
          },
          {
            text: 'Imprimir canhotos',
            disabled: true
          }
        ];
      }
      return [
        {
          text: 'Início',
          disabled: false,
          to: '/start'
        },
        {
          text: 'Embarques',
          disabled: false,
          to: '/shipments',
          exact: true
        },
        {
          text: 'Gerenciar canhotos',
          disabled: true
        }
      ];
    }
  },

  watch: {
    showNoReceiptNfes: {
      async handler(value) {
        await this.fetchAll(value);
        this.selectedReceipts = [];
      }
    }
  },

  created() {
    this.fetchAll();
    if (this.isShipperDeliveryReceiptManagement) {
      ReceiptsManagementHandler.setPrintMode(this, true);
    } else {
      ReceiptsManagementHandler.setPrintMode(this, false);
    }
  },
  methods: {
    ...mapActions('ShipmentStore', ['getReceiptsByHierarchies']),
    ...mapActions('NfeStore', ['getSrcImage', 'appovreNfeReceipt', 'deleteNfeReceipt', 'createReceiptNfe']),
    voltar() {
      this.$router.back();
    },
    openDeleteDialog(idToDelete = null) {
      this.receiptIdToDelete = idToDelete;
      this.isDeleteDialogOpen = true;
    },
    openApproveDialog() {
      this.getSelectedReceipts();
      this.isApproveDialogOpen = true;
    },
    async openUpdateCompletionDateDialog() {
      this.nfeIds = await this.getNfeIdsSelectedReceipts();
      this.isUpdateCompletionDateDialogOpen = true;
    },
    resetIdToDelete() {
      this.receiptIdToDelete = null;
    },
    resetIdToApprove() {
      this.receiptIdToApprove = null;
    },
    getApproveUserName(name) {
      return name ? `por ${name.split(' ')[0]}` : '';
    },

    handleSelectAll() {
      if (this.areAllReceiptsSelected) {
        ReceiptsManagementHandler.removeAllReceipts(this);
      } else {
        ReceiptsManagementHandler.selectAllReceipts(this);
      }
    },

    handleFeedbackMessage(type = 'success', message = 'Sucesso!') {
      this.errorsType = type;
      this.errors = [message];
    },

    async handleUploadReceipt(nfeId, body) {
      const params = {
        id: nfeId,
        body: body
      };
      try {
        await this.createReceiptNfe(params);
        await this.fetchAll(this.showNoReceiptNfes);

        this.handleFeedbackMessage('success', 'Canhoto anexado com sucesso!');
      } catch (e) {
        console.error(e);
        this.handleFeedbackMessage('error', 'Opss, algo deu errado!');
      }
    },

    async handleDeleteReceipt() {
      try {
        this.isDeleteButtonLoading = true;

        if (this.receiptIdToDelete) {
          const data = await this.deleteNfeReceipt(this.receiptIdToDelete);
          this.receipts = this.receipts.filter((receipt) => receipt.id !== this.receiptIdToDelete);
          this.selectedReceipts = this.selectedReceipts.filter((id) => id !== this.receiptIdToDelete);
          if (data.success) {
            this.handleFeedbackMessage('success', 'Canhoto deletado com sucesso!');
          } else {
            this.handleFeedbackMessage('error', 'Erro ao deletar canhoto');
          }
        } else {
          const selectedReceipts = this.getSelectedReceipts().map((receipt) => receipt.toString());
          const data = await this.deleteNfeReceipt(selectedReceipts);
          selectedReceipts.map((id) => {
            this.receipts = this.receipts.filter((receipt) => receipt.id !== id);
          });
          this.selectedReceipts = [];
          if (data.success) {
            this.handleFeedbackMessage('success', 'Canhoto deletado com sucesso!');
          } else {
            this.handleFeedbackMessage('error', 'Erro ao deletar canhoto');
          }
        }
      } catch (error) {
        console.error(error);
        this.handleFeedbackMessage('error', 'Ops, algo deu errado!');
      } finally {
        this.setSelectedReceipts(this.receipts);
        this.savedReceipts = this.receipts;

        this.isDeleteDialogOpen = false;
        this.isDeleteButtonLoading = false;
      }
    },

    async approveReceipt(receiptId) {
      try {
        await this.appovreNfeReceipt([receiptId]);
        this.receipts.find((receipt) => receipt.id === receiptId).isValidated = true;
        this.handleFeedbackMessage('success', 'Canhoto aprovado com sucesso!');
      } catch (error) {
        console.error(error);
      }
    },

    async approveMultipleReceipts() {
      try {
        this.isApproveButtonLoading = true;
        const selectedReceipts = this.getSelectedReceipts();
        await this.appovreNfeReceipt([...selectedReceipts]);
        selectedReceipts.map((id) => {
          this.receipts.find((receipt) => receipt.id === id).isValidated = true;
        });
        this.handleFeedbackMessage('success', 'Canhoto(s) aprovado(s) com sucesso!');
      } catch (error) {
        console.error(error);
      } finally {
        this.isApproveDialogOpen = false;
        this.isApproveButtonLoading = false;
      }
    },

    onRemoveFromPrint(receiptNfeId) {
      this.receipts = this.receipts.filter((receipt) => receipt.nfeId !== receiptNfeId);
    },

    onReceiptAppend(paramns) {
      const { file, nfeId } = paramns;
      let receiptToUpload = this.receipts.find((receipt) => receipt.nfeId === nfeId);

      const formData = new FormData();

      formData.append('file', file);

      receiptToUpload.receiptToUploadFile = formData;

      this.handleUploadReceipt(nfeId, receiptToUpload.receiptToUploadFile);
    },

    async fetchAll(hasNoReceiptNfes = true) {
      this.loading = true;
      this.receipts = [];
      const body = {
        stopIds: this.getSelectedChecbox
          .filter((item) => {
            if (item.type === 'Parada' || item.type === 'Stop') {
              return item.id;
            }
          })
          .map((item) => item.id),
        deliveryIds: this.getSelectedChecbox
          .filter((item) => {
            if (item.type === 'Entrega') {
              return item.id;
            }
          })
          .map((item) => item.id),
        cargoIds: this.getSelectedChecbox
          .filter((item) => {
            if (item.type === 'Carga' || item.type === 'Cargo') {
              return item.id;
            }
          })
          .map((item) => item.id),
        boardingIds: this.getSelectedChecbox
          .filter((item) => {
            if (item.type === 'Embarque') {
              return item.id;
            }
          })
          .map((item) => item.id),
        nfeIds: this.getSelectedChecbox
          .filter((item) => {
            if (item.type === 'Nota Fiscal' || item.type === 'Nfe') {
              return item.id;
            }
          })
          .map((item) => item.id)
      };

      try {
        const data = await this.getReceiptsByHierarchies(body);
        const receipts = data.filter((receipt) => receipt.linkDownload);

        this.setSelectedReceipts([...receipts]);

        for (let item of data) {
          try {
            if (item.linkDownload) {
              const urlImage = `${baseURL}pt/${item.linkDownload}`;
              const imgBlob = await this.getSrcImageCanhoto(urlImage);
              try {
                const dataFile = await this.toBase64(imgBlob);

                let newItem = {
                  ...item,
                  expanded: false,
                  selected: false,
                  srcImage: dataFile
                };

                setTimeout(() => {
                  this.receipts.push(newItem);
                }, 10);
              } catch (e) {
                console.error(e);
              }
            } else if (hasNoReceiptNfes) {
              let newItem = {
                ...item,
                expanded: false,
                selected: false,
                receiptToUploadFile: null,
                srcImage: null
              };

              this.receipts.push(newItem);
            }
          } catch (e) {
            console.error(e);
          }
        }
        this.savedReceipts = this.receipts;

        this.loading = false;
      } catch (e) {
        console.error(e);
      }
    },
    async getSrcImageCanhoto(endpoint) {
      try {
        const data = await this.getSrcImage(endpoint);

        return data;
      } catch (e) {
        console.error(e);
        this.errorsType = 'error';
        this.errors = ['Opss, algo deu errado!'];
        this.error500 = e;
      }
    },

    setSelectedReceipts(receipts) {
      const receiptsToStore = receipts.reduce((acc, cur) => {
        return { ...acc, [cur.id]: false };
      }, {});
      ReceiptsManagementHandler.setAllSelectedReceipts(this, receiptsToStore);
    },

    getSelectedReceipts() {
      const receipts = ReceiptsManagementHandler.getSelectedReceipts(this);
      const selectedReceipts = Object.keys(receipts).filter((receiptId) => receipts[receiptId]);
      return selectedReceipts;
    },

    async getNfeIdsSelectedReceipts() {
      const receiptIds = this.getSelectedReceipts();
      const nfeIds = receiptIds.map((receiptId) => this.receipts.find((receipt) => receipt.id === receiptId).nfeId);
      return nfeIds;
    },

    printReceipts() {
      window.print();
    },

    removeMultipleReceiptsFromPrint() {
      const selectedReceipts = this.getSelectedReceipts();
      selectedReceipts.forEach((receiptId) => {
        this.receipts = this.receipts.filter((receipt) => receipt.id !== receiptId);
      });
      this.setSelectedReceipts(this.receipts);
    },

    restoreToPrint() {
      const receipts = this.savedReceipts.filter((receipt) => receipt.linkDownload);
      this.receipts = this.savedReceipts;
      this.setSelectedReceipts([...receipts]);
    },

    handlePrintMode() {
      const receipts = this.savedReceipts.filter((receipt) => receipt.linkDownload);
      if (this.printMode) {
        this.receipts = this.savedReceipts;
        this.setSelectedReceipts([...receipts]);
      } else {
        const noImgReceipts = this.savedReceipts
          .filter((receipt) => !receipt.linkDownload)
          .map((receipt, i) => {
            return { ...receipt, id: receipt.id + i };
          });
        this.receipts = [...receipts, ...noImgReceipts];
        this.setSelectedReceipts([...receipts, ...noImgReceipts]);
      }
      ReceiptsManagementHandler.setPrintMode(this, !this.printMode);
      ReceiptsManagementHandler.removeAllReceipts(this);
    },

    async shouldUpdateList() {
      this.nfeIds = [];
      await this.fetchAll();
    }
  }
};
</script>

<style lang="scss" scoped>
/* ===== Scrollbar CSS ===== */
.receipts {
  scrollbar-width: thin;
  scrollbar-color: #c7c7c7 #ffffff;
  margin-right: 10px;
}

.receipts::-webkit-scrollbar {
  width: 6px;
  margin-right: 10px;
}

.receipts::-webkit-scrollbar-track {
  background: #ffffff;
  margin-right: 10px;
}

.receipts::-webkit-scrollbar-thumb {
  background-color: #c7c7c7;
  border-radius: 10px;
  margin-right: 10px;
}

.wrapper-to-print {
  display: none;
}

@media print {
  .pagebreak {
    page-break-before: always;
  }
  .receipts-management {
    position: absolute;
    width: 100%;
    z-index: 999;
    background: #fff;
    left: 0;
    right: 0;
  }
  .print-wrapper {
    width: 100%;
  }
  .wrapper-to-print {
    display: inline-block;
  }
  img {
    max-width: 600px;
  }
}
</style>
